import { useMemo, type ReactNode } from 'react';
import { useControllableState } from '../../hooks/useControllableState.js';

export type PaginationSize = 'sm' | 'md';

export interface PaginationProps {
  /** 1-based current page. */
  page?: number;
  defaultPage?: number;
  onPageChange?: (page: number) => void;
  /** Total number of pages. When <=1, the component renders nothing. */
  pageCount: number;
  /** How many sibling pages to show on each side of current. Default 1. */
  siblingCount?: number;
  /** Show first / last edge buttons. Default true. */
  showEdges?: boolean;
  size?: PaginationSize;
  /** Label for the whole nav landmark. */
  'aria-label'?: string;
  /** Per-button labels for screen readers. */
  labels?: {
    first?: string;
    previous?: string;
    next?: string;
    last?: string;
    page?: (n: number) => string;
  };
  className?: string;
}

/** Compute the sliding window of page numbers to display, with ellipses
 *  on the sides when the window clips. Produces values like:
 *    1 … 4 5 [6] 7 8 … 20
 *  or at edges:
 *    [1] 2 3 4 5 … 20
 *  Ellipses are represented by `null` in the returned array. */
function windowPages(current: number, total: number, siblings: number): Array<number | null> {
  if (total <= 0) return [];
  const first = 1;
  const last = total;
  const left = Math.max(first, current - siblings);
  const right = Math.min(last, current + siblings);
  const showLeftEllipsis = left > first + 1;
  const showRightEllipsis = right < last - 1;

  const out: Array<number | null> = [];
  out.push(first);
  if (showLeftEllipsis) out.push(null);
  else for (let p = first + 1; p < left; p++) out.push(p);
  for (let p = left; p <= right; p++) {
    if (p !== first && p !== last) out.push(p);
  }
  if (showRightEllipsis) out.push(null);
  else for (let p = right + 1; p < last; p++) out.push(p);
  if (last !== first) out.push(last);

  /* Dedupe in case of short sequences (window ends touching edges). */
  const seen = new Set<number | null>();
  return out.filter((p) => {
    if (p === null) return true;
    if (seen.has(p)) return false;
    seen.add(p);
    return true;
  });
}

/** Paginator for tables, lists, and long query-result views.
 *
 *  Renders a landmark `<nav>` with:
 *    ⟵ first | ← prev | 1 … n [m] … N | next → | last ⟶
 *
 *  Page-number buttons use `aria-current="page"` on the active one so
 *  screen readers announce it. The landmark label is read first so
 *  users can distinguish between multiple paginators on the same page
 *  (e.g., table-top and table-bottom copies).
 */
export function Pagination(props: PaginationProps): ReactNode {
  const {
    page,
    defaultPage = 1,
    onPageChange,
    pageCount,
    siblingCount = 1,
    showEdges = true,
    size = 'md',
    labels,
    className,
    'aria-label': ariaLabel = 'Pagination',
  } = props;

  const [current, setCurrent] = useControllableState<number>({
    defaultValue: defaultPage,
    ...(page !== undefined ? { value: page } : {}),
    ...(onPageChange !== undefined ? { onChange: onPageChange } : {}),
  });

  /* Sanitise pageCount: negative / NaN / Infinity get coerced to 0 so
   * downstream window math doesn't produce garbage. */
  const safePageCount = Number.isFinite(pageCount) && pageCount > 0 ? Math.floor(pageCount) : 0;
  /* Clamp current page into [1, safePageCount] to prevent out-of-range
   * values from callers that passed e.g. page=100 against pageCount=10. */
  const safeCurrent =
    safePageCount === 0 ? 1 : Math.min(safePageCount, Math.max(1, Math.floor(current)));

  const pages = useMemo(
    () => windowPages(safeCurrent, safePageCount, siblingCount),
    [safeCurrent, safePageCount, siblingCount],
  );

  if (safePageCount <= 1) return null;

  const firstLabel = labels?.first ?? 'First page';
  const prevLabel = labels?.previous ?? 'Previous page';
  const nextLabel = labels?.next ?? 'Next page';
  const lastLabel = labels?.last ?? 'Last page';
  const pageLabel = labels?.page ?? ((n: number) => `Page ${String(n)}`);

  const go = (to: number): void => {
    if (to < 1 || to > safePageCount) return;
    setCurrent(to);
  };

  const classes = ['vq-pagination', `vq-pagination--${size}`, className].filter(Boolean).join(' ');

  const isFirst = safeCurrent <= 1;
  const isLast = safeCurrent >= safePageCount;

  return (
    <nav className={classes} aria-label={ariaLabel} data-component="Pagination">
      {showEdges && (
        <button
          type="button"
          className="vq-pagination__btn"
          onClick={() => {
            go(1);
          }}
          disabled={isFirst}
          aria-label={firstLabel}
        >
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
            <polyline
              points="8 2 4 6 8 10"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <polyline
              points="5 2 1 6 5 10"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      )}
      <button
        type="button"
        className="vq-pagination__btn"
        onClick={() => {
          go(safeCurrent - 1);
        }}
        disabled={isFirst}
        aria-label={prevLabel}
      >
        <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
          <polyline
            points="8 2 4 6 8 10"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>
      <ul className="vq-pagination__list">
        {pages.map((p, i) =>
          p === null ? (
            <li
              key={`ellipsis-${String(i)}`}
              className="vq-pagination__ellipsis"
              aria-hidden="true"
            >
              …
            </li>
          ) : (
            <li key={p}>
              <button
                type="button"
                className="vq-pagination__btn vq-pagination__btn--page"
                onClick={() => {
                  go(p);
                }}
                aria-label={pageLabel(p)}
                aria-current={p === safeCurrent ? 'page' : undefined}
                data-active={p === safeCurrent ? 'true' : 'false'}
              >
                {p}
              </button>
            </li>
          ),
        )}
      </ul>
      <button
        type="button"
        className="vq-pagination__btn"
        onClick={() => {
          go(safeCurrent + 1);
        }}
        disabled={isLast}
        aria-label={nextLabel}
      >
        <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
          <polyline
            points="4 2 8 6 4 10"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>
      {showEdges && (
        <button
          type="button"
          className="vq-pagination__btn"
          onClick={() => {
            go(safePageCount);
          }}
          disabled={isLast}
          aria-label={lastLabel}
        >
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
            <polyline
              points="4 2 8 6 4 10"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <polyline
              points="7 2 11 6 7 10"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      )}
    </nav>
  );
}
